import axios from 'axios';
import { config } from '../config';
import { preferredLocale } from '../i18n/locales';
import { apiEventFor, currentSessionId, track } from '../analytics/track';
import { noteRequestFailure, reportReachable } from './connection';
import { isSteadilySignedIn } from '../context/steadySignedIn';
import { PRO_CODES, proWall } from '../utils/proWall.js';
import { deviceId } from '../utils/deviceId.js';

// Create an Axios instance with base configuration
// How long the page waits for one call of the agent's loop (see agentTurn)
export const AGENT_TURN_WAIT = 90000;

const axiosInstance = axios.create({
  baseURL: `${config.API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for CORS with credentials
});

// While Clerk flickers to "no session" for someone still signed in, a request
// waits this long for the session to come back rather than going out without a
// token and coming back 401 (context/steadySignedIn.js)
const SESSION_WAIT_MS = 3000;
const SESSION_POLL_MS = 150;

const currentSession = () => {
  const clerk = typeof window !== 'undefined' ? window.Clerk : undefined;
  return clerk && clerk.session ? clerk.session : undefined;
};

// Resolve a fresh Clerk token per request (best practice: don't persist tokens)
export async function fetchClerkToken({ waitMs = SESSION_WAIT_MS } = {}) {
  let session = currentSession();
  // Only someone the app takes to be signed in is waited for: a signed-out
  // visitor (the public checker, the generators) still gets null at once
  for (let waited = 0; !session && isSteadilySignedIn() && waited < waitMs; waited += SESSION_POLL_MS) {
    await new Promise((resolve) => setTimeout(resolve, SESSION_POLL_MS));
    session = currentSession();
  }
  if (!session) return null;
  const token = await session.getToken();
  return token || null;
}

function clientTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

// Add request interceptor to include a fresh auth token on every request
axiosInstance.interceptors.request.use(async (config) => {
  if (config.baseURL && config.url && `${config.baseURL}${config.url}`.startsWith(axiosInstance.defaults.baseURL)) {
    const token = await fetchClerkToken();
    config.headers = config.headers || {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // The language this person reads the site in, for what the API writes to
    // them later (the project-ready email)
    config.headers['X-Client-Locale'] = preferredLocale();
    // Which replay this is, so a refusal the API records can be watched: the
    // two people refused their second free video had no session on the event
    const session = currentSessionId();
    if (session) config.headers['X-PostHog-Session'] = session;
    // This browser, so a second free account on it is told apart (utils/deviceId.js)
    const device = deviceId();
    if (device) config.headers['X-Client-Device'] = device;
    // The device's own time zone, kept beside the country Cloudflare gives the request: a mismatch (Asia/Tehran
    // seen from a German IP) is a VPN from a blocked country (the API's services/vpn_watch.py; measured only)
    const zone = clientTimeZone();
    if (zone) config.headers['X-Client-TZ'] = zone;
    // Who a change to an AI Agent video is by, for its version (the API's services/versions.py): the
    // page marks the writes it makes for the agent's steps
    if (Date.now() < agentWritesUntil) config.headers['X-Change-By'] = 'agent';
  }
  return config;
});

// A write to an AI Agent video landed: the page's version history asks for the versions again
// (components/AIAgent/editor/VersionHistory.jsx). Not the assistant's plan, the agent's turns or the versions themselves.
const VIDEO_WRITE = /^\/ai-agent\/projects\/([^/]+)\/(?!assistant|versions|agent(?:$|[/?]))/;
const noteVideoSaved = (config) => {
  if (!config?.url || !/^(put|post|delete|patch)$/i.test(config.method || '')) return;
  const found = config.url.replace(/^https?:\/\/[^/]+/, '').match(VIDEO_WRITE);
  if (found && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('ai-agent-video-saved', { detail: { projectId: found[1] } }));
};

let agentWritesUntil = 0;
/** The writes of the next `ms` milliseconds are the agent's (its steps save through the page's own routes, debounced). */
export const markAgentWrites = (ms = 3000) => { agentWritesUntil = Math.max(agentWritesUntil, Date.now() + ms); };
export const isAgentWriting = () => Date.now() < agentWritesUntil;

// The product moments (a project made, a clip exported) and server failures,
// recorded where every page's call passes through
const recordApiEvent = (config, status, code) => {
  const event = apiEventFor(config?.method, config?.url, status, config?.data, code);
  if (event) track(...event);
};

// Response interceptor (keep for logging or global error handling)
axiosInstance.interceptors.response.use(
  (response) => {
    reportReachable();
    recordApiEvent(response.config, response.status);
    noteVideoSaved(response.config);
    return response;
  },
  (error) => {
    console.error(`Axios Response Error: ${error.response?.status} from ${error.config?.url}`, error);
    // A Pro feature the free plan reached for: the one offer (utils/proWall.js)
    const proReason = PRO_CODES[error.response?.data?.detail?.code];
    if (error.response?.status === 403 && proReason) proWall(proReason, 'api');
    noteRequestFailure(error);
    recordApiEvent(error.config, error.response?.status ?? 0, axios.isCancel(error) ? 'ERR_CANCELED' : error.code);
    return Promise.reject(error);
  }
);

export const api = {
  // Authentication endpoints
  getCurrentUser: () => axiosInstance.get('/auth/me').then(res => res.data),
  
  // YouTube video info
  getVideoInfo: (url) =>
    axiosInstance.get(`/videos/info?url=${encodeURIComponent(url)}`)
      .then(res => res.data),

  // File Upload
  initiateUpload: (data) => axiosInstance.post('/uploads/initiate', data).then(res => res.data),

  // Project endpoints
  getProjects: (params = {}) => {
    const { page = 1, pageSize = 10, projectType = null } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('page_size', pageSize);
    if (projectType) {
      queryParams.append('project_type', projectType);
    }
    return axiosInstance.get(`/projects?${queryParams.toString()}`).then(res => res.data);
  },

  getProject: (id) => axiosInstance.get(`/projects/${id}`).then(res => res.data),
  // The user's most recent project of this type for the same video, if any ({ project: … | null })
  findProjectByUrl: (url, type) =>
    axiosInstance.get('/projects/by-url', { params: { url, type } }).then(res => res.data.project),

  createProject: (data, params = {}) => axiosInstance.post('/projects', data, { params }).then(res => res.data),

  // Transcription-only project creation
  createTranscriptionProject: (data) => axiosInstance.post('/projects/transcription', data).then(res => res.data),

  // Reframe-only project creation
  createReframeProject: (data) => axiosInstance.post('/projects/reframe', data).then(res => res.data),

  // Dispatch reframing for an existing project
  reframeProject: (projectId, data) => axiosInstance.post(`/projects/${projectId}/reframe`, data).then(res => res.data),

  // Reframe's live preview: the crop analysis the page draws any shape from,
  // so another shape costs an analysis rather than another render
  requestReframePreview: (projectId, targetAspectRatio) =>
    axiosInstance.post(`/projects/${projectId}/reframe-preview`, { target_aspect_ratio: targetAspectRatio }).then(res => res.data),
  getReframePreview: (projectId, rev) =>
    axiosInstance.get(`/projects/${projectId}/reframe-preview`, { params: rev ? { rev } : undefined }).then(res => res.data),
  // The per-shot framing set over that analysis; null goes back to it
  updateReframeOverrides: (projectId, overrides) =>
    axiosInstance.put(`/projects/${projectId}/reframe-overrides`, { overrides }).then(res => res.data),

  // Transcription-only helper: trigger standalone transcription for a project
  transcribeProject: (projectId) =>
    axiosInstance.post(`/projects/${projectId}/transcribe`).then(res => res.data),

  // Auto Caption: the user's word fixes on the whole video's transcript,
  // in the same shape a clip stores its own; null restores what was heard
  // Speaker names and fixes on an Auto Caption project (utils/speakers.js):
  // {turns, names}; null restores what was found. Answers {speakers}
  updateProjectSpeakers: (projectId, edits) =>
    axiosInstance.put(`/projects/${projectId}/speakers`, { edits })
      .then(res => res.data),

  updateProjectCaptionEdits: (projectId, edits) =>
    axiosInstance.put(`/projects/${projectId}/caption-edits`, { edits }).then(res => res.data),

  // Auto Caption: Render video with styled captions
  renderCaptionedVideo: (projectId, captionStyle) =>
    axiosInstance.post(`/projects/${projectId}/render-captions`, { caption_style: captionStyle }).then(res => res.data),

  deleteProject: (id) => axiosInstance.delete(`/projects/${id}`).then(res => res.data),

  // `data` may carry a changed prompt for the analysis ({ prompt })
  retryProject: (id, data) => axiosInstance.post(`/projects/${id}/retry`, data ?? {}).then(res => res.data),

  // Segment endpoints
  updateSegment: (projectId, shortId, data) =>
    axiosInstance.put(`/projects/${projectId}/shorts/${shortId}/update`, data)
      .then(res => res.data),

  // Extract short endpoints — optional body with captions + captionStyle for one-step export,
  // and studio_sound: true to clean the voice up after the render
  extractShort: (projectId, shortId, data = null) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/extract`, data)
      .then(res => res.data),

  // Reprompt a project with custom prompt
  repromptProject: (projectId, data) =>
    axiosInstance.post(`/projects/${projectId}/reprompt`, data)
      .then(res => res.data),

  // Get short
  getShort: (projectId, shortId) =>
    axiosInstance.get(`/projects/${projectId}/shorts/${shortId}`)
      .then(res => res.data),

  // Get detailed short data including captions and video URL
  getShortDetails: (projectId, shortId) =>
    axiosInstance.get(`/projects/${projectId}/shorts/${shortId}/details`)
      .then(res => res.data),

  // Render short with captions
  renderShort: (projectId, shortId, data) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/render`, data)
      .then(res => res.data),

  // Crop windows endpoints. getCropWindows returns a clip's preview data
  // (crop windows + captions); pass its crop_windows_rev so the response can
  // be cached — a revision's content never changes
  getCropWindows: (projectId, shortId, rev) =>
    axiosInstance.get(`/projects/${projectId}/shorts/${shortId}/crop-windows`, {
      params: rev == null ? undefined : { rev },
    })
      .then(res => res.data),

  // The user's per-shot crop edits (see utils/cropOverrides); [] restores
  // the automatic framing
  updateCropOverrides: (projectId, shortId, overrides) =>
    axiosInstance.put(`/projects/${projectId}/shorts/${shortId}/crop-overrides`, { overrides })
      .then(res => res.data),

  // The user's caption text edits (see utils/captionEdits); null restores
  // the prepared words
  updateCaptionEdits: (projectId, shortId, edits) =>
    axiosInstance.put(`/projects/${projectId}/shorts/${shortId}/caption-edits`, { edits })
      .then(res => res.data),

  // Pauses and filler sounds taken out of a clip (see utils/clipCuts), source
  // ms; [] puts everything back. Every export plays the clip without them
  // The clip's own caption style ({enabled: false} = no captions); exports
  // that send none use it. Answers {caption_style, crop_windows_rev}
  updateClipCaptionStyle: (projectId, shortId, captionStyle) =>
    axiosInstance.put(`/projects/${projectId}/shorts/${shortId}/caption-style`, { caption_style: captionStyle })
      .then(res => res.data),

  // A copy of a clip (optionally over its own range, source seconds); answers the new clip
  duplicateShort: (projectId, shortId, range = null) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/duplicate`, range || {})
      .then(res => res.data),

  // Cut a clip in two at `at` (source seconds): answers {first, second}
  splitShort: (projectId, shortId, at) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/split`, { at })
      .then(res => res.data),

  // Speaker names and fixes on a clip's prepared turns (utils/speakers.js):
  // {turns, names}; null restores what was found. Answers {crop_windows_rev}
  updateClipSpeakers: (projectId, shortId, edits) =>
    axiosInstance.put(`/projects/${projectId}/shorts/${shortId}/speakers`, { edits })
      .then(res => res.data),

  updateCuts: (projectId, shortId, cuts) =>
    axiosInstance.put(`/projects/${projectId}/shorts/${shortId}/cuts`, { cuts })
      .then(res => res.data),

  // The clip's pauses, found in its sound (word timings hide them): cuts that
  // shorten each long pause to a breath, source ms. Nothing is saved
  suggestPauseCuts: (projectId, shortId) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/cuts/suggest`)
      .then(res => res.data.cuts),

  // Clip polish (see utils/clipPolish): the writer's hook title and moments,
  // saved and returned; then the user's edits. Saving null-ish clears it
  // Caption emoji picked by the model for the clip (api/services/clip_emoji.py):
  // answers {emoji_picks: {picks: [{atMs, code}]}, crop_windows_rev}
  pickClipEmoji: (projectId, shortId) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/emoji`)
      .then(res => res.data),

  // The same for an Auto Caption video: answers {emoji_picks}
  // A clip's dubs (routes/dubs.py): {languages, spoken, ready, speakers, dubs}
  listClipDubs: (projectId, shortId) =>
    axiosInstance.get(`/projects/${projectId}/shorts/${shortId}/dubs`).then(res => res.data),

  // A voice for each speaker, suggested from how they sound: {voices: {label: voice}}
  suggestDubVoices: (projectId, shortId, chosen = {}) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/dubs/voices`, { chosen }).then(res => res.data),

  createClipDub: (projectId, shortId, { language, voices }) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/dubs`, { language, voices }).then(res => res.data),

  exportClipDub: (projectId, shortId, language, captionStyle = null) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/dubs/${language}/export`,
      captionStyle ? { caption_style: captionStyle } : {}).then(res => res.data),

  deleteClipDub: (projectId, shortId, language) =>
    axiosInstance.delete(`/projects/${projectId}/shorts/${shortId}/dubs/${language}`).then(res => res.data),

  pickProjectEmoji: (projectId) =>
    axiosInstance.post(`/projects/${projectId}/emoji`)
      .then(res => res.data),

  // The emoji the user left on the words (the transcript's picker): the whole
  // list [{atMs, code}], replacing the model's. Answers like the picks above
  updateClipEmoji: (projectId, shortId, picks) =>
    axiosInstance.put(`/projects/${projectId}/shorts/${shortId}/emoji`, { picks })
      .then(res => res.data),

  updateProjectEmoji: (projectId, picks) =>
    axiosInstance.put(`/projects/${projectId}/emoji`, { picks })
      .then(res => res.data),

  writeClipPolish: (projectId, shortId) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/polish`)
      .then(res => res.data),
  updateClipPolish: (projectId, shortId, polish) =>
    axiosInstance.put(`/projects/${projectId}/shorts/${shortId}/polish`, polish || { hook: null, moments: [] })
      .then(res => res.data),

  reanalyzeCropWindows: (projectId, shortId) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/crop-windows/reanalyze`)
      .then(res => res.data),
  
  // New backend rendering for editor compositions
  renderEditorComposition: (data) => axiosInstance.post('/render/editor', data).then(res => res.data),

  getRenderJobStatus: (jobId) => axiosInstance.get(`/render/editor/${jobId}`).then(res => res.data),

  // The editor's sessions, kept per account (routes/editor_sessions.py)
  // A time limit: a hung API must not hold the editor on "Opening your project…"
  getEditorSession: (key, timeout = 5000) => axiosInstance.get(`/editor/sessions/${encodeURIComponent(key)}`, { timeout }).then(res => res.data),
  putEditorSession: (key, state) => axiosInstance.put(`/editor/sessions/${encodeURIComponent(key)}`, { state }).then(res => res.data),
  deleteEditorSession: (key) => axiosInstance.delete(`/editor/sessions/${encodeURIComponent(key)}`).then(res => res.data),

  // The signed-in user's recent editor exports, newest first, links re-signed
  listRenderJobs: (limit = 20) => axiosInstance.get('/render/editor', { params: { limit } }).then(res => res.data),

  // Subscription endpoints
  // sessionId: the success page's checkout, which the API marks as reported by this page and answers `paid` for
  getCurrentSubscription: (sessionId) => axiosInstance.get('/subscription/current', sessionId ? { params: { session_id: sessionId } } : undefined).then(res => res.data),

  createPortalSession: (returnUrl) =>
    axiosInstance.post('/subscription/create-portal-session', { return_url: returnUrl }).then(res => res.data),

  // fromLimit: they came from a wall, which is what earns the first-month
  // coupon (the API decides; the browser only says where they came from)
  // reason: what sent them (the upgrade offer's), kept with the checkout so a checkout left
  // can be offered again for the same thing (services/checkout_waiting.py)
  createCheckoutSession: (priceId, quantity = 1, { fromLimit = false, reason } = {}) =>
    axiosInstance.post('/subscription/create-checkout-session', {
      price_id: priceId,
      quantity: quantity,
      ...(fromLimit && { from_limit: true }),
      ...(reason && { reason }),
    }).then(res => res.data),

  // A pack of AI videos paid once, no subscription (services/packs.py)
  createPackCheckout: (reason) =>
    axiosInstance.post('/subscription/create-pack-checkout', { reason }).then(res => res.data),
  confirmPack: (sessionId) =>
    axiosInstance.post('/subscription/pack/confirm', { session_id: sessionId }).then(res => res.data),
  // The Clip pack (api/services/clip_packs.py): bought from a video, confirmed on the way back to it, and put on an
  // older video of theirs
  createClipPackCheckout: (projectId, reason) =>
    axiosInstance.post('/subscription/create-clip-pack-checkout', { project_id: projectId, reason }).then(res => res.data),
  confirmClipPack: (sessionId) =>
    axiosInstance.post('/subscription/clip-pack/confirm', { session_id: sessionId }).then(res => res.data),
  unlockWithClipPack: (projectId) =>
    axiosInstance.post(`/projects/${projectId}/clip-pack/unlock`).then(res => res.data),
  // A checkout they opened and left, offered again on this visit, and what stopped them
  getWaitingCheckout: () => axiosInstance.get('/subscription/waiting').then(res => res.data),
  answerWaitingCheckout: (answer) => axiosInstance.post('/subscription/waiting', { answer }).then(res => res.data),
  
  // Usage endpoints
  getUsageMetrics: () => axiosInstance.get('/usage/metrics').then(res => res.data),

  // Which emails the user gets (api/routes/email.py)
  getEmailPreferences: () => axiosInstance.get('/email/preferences').then(res => res.data),
  updateEmailPreferences: (changes) => axiosInstance.put('/email/preferences', changes).then(res => res.data),

  checkProjectUsage: () => axiosInstance.post('/usage/check/project').then(res => res.data),

  checkExtractedShortUsage: () => axiosInstance.post('/usage/check/extracted-short').then(res => res.data),

  // AI Agent endpoints (ADK session-based conversation flow)
  createAIAgentProject: (data) => axiosInstance.post('/ai-agent/projects', data).then(res => res.data),

  // An AI Agent video's public page: turn it on or off (owner), read it (anyone)
  setVideoSharing: (projectId, enabled) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/share`, { enabled }).then(res => res.data),
  getSharedVideo: (projectId) => axiosInstance.get(`/public/videos/${projectId}`).then(res => res.data),
  // The public gallery (api routes/gallery.py): the owner's switch, and the public pages' reads
  setVideoGallery: (projectId, enabled) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/gallery`, { enabled }).then(res => res.data),
  // The owner's caption for their gallery post, or null for none (the default)
  setVideoGalleryCaption: (projectId, caption) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/gallery/caption`, { caption }).then(res => res.data),
  getGallery: (locale, page = 1) => axiosInstance.get('/public/gallery', { params: { locale, page } }).then(res => res.data),
  getGalleryVideo: (projectId) => axiosInstance.get(`/public/gallery/${projectId}`).then(res => res.data),
  getGalleryQueue: () => axiosInstance.get('/admin/gallery').then(res => res.data),
  reviewGalleryVideo: (projectId, approve, reason, approveCaption = true) =>
    axiosInstance.post(`/admin/gallery/${projectId}/review`, { approve, reason, approve_caption: approveCaption }).then(res => res.data),
  // The brand kit: a logo on every clip, and the caption style clips start with
  getBrandKit: () => axiosInstance.get('/brand-kit').then(res => res.data),
  updateBrandKit: (patch) => axiosInstance.put('/brand-kit', patch).then(res => res.data),
  uploadBrandLogo: (file) => {
    const form = new FormData();
    form.append('file', file);
    return axiosInstance.post('/brand-kit/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } }).then(res => res.data);
  },
  deleteBrandLogo: () => axiosInstance.delete('/brand-kit/logo').then(res => res.data),
  // B-roll: stock shots over a clip (backend services/clip_broll.py)
  searchBroll: (projectId, shortId, q) =>
    axiosInstance.get(`/projects/${projectId}/shorts/${shortId}/broll/search`, { params: { q } }).then(res => res.data.shots),
  // `exclude`: the shots the clip already has ({id, atMs, durationMs}), so the AI looks elsewhere
  suggestBroll: (projectId, shortId, exclude = []) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/broll/suggest`, { exclude }).then(res => res.data.suggestions),
  // An exported clip's public page (the same /v/<id>, with the clip's id)
  setClipSharing: (projectId, shortId, enabled) =>
    axiosInstance.post(`/projects/${projectId}/shorts/${shortId}/share`, { enabled }).then(res => res.data),

  // Sharing a video earns a video (api/services/referrals.py): say which shared
  // video a new account came from, and read how one's own sharing is going
  claimReferral: (projectId) => axiosInstance.post('/referrals/claim', { project_id: projectId }).then(res => res.data),
  claimInvite: (inviterId) => axiosInstance.post('/referrals/claim', { inviter_id: inviterId }).then(res => res.data),
  getMyReferrals: () => axiosInstance.get('/referrals/me').then(res => res.data),
  // Where a new account came from (api/services/signup_source.py), said once
  keepSignupSource: (touch) => axiosInstance.post('/signup-source', touch).then(res => res.data),

  // The affiliate program (api/routes/affiliates.py): a link's first visit, the claim a
  // new account makes, and an affiliate's own page. getMyAffiliate is 404 before joining.
  recordAffiliateVisit: (code) => axiosInstance.post(`/public/affiliates/${encodeURIComponent(code)}/visit`).then(res => res.data),
  claimAffiliate: (code, firstSeenAt) =>
    axiosInstance.post('/affiliates/claim', { code, first_seen_at: firstSeenAt }).then(res => res.data),
  joinAffiliates: (payoutMethod, payoutDetails) =>
    axiosInstance.post('/affiliates/join', { payout_method: payoutMethod, payout_details: payoutDetails }).then(res => res.data),
  getMyAffiliate: () => axiosInstance.get('/affiliates/me').then(res => res.data),
  updateMyAffiliate: (payoutMethod, payoutDetails) =>
    axiosInstance.put('/affiliates/me', { payout_method: payoutMethod, payout_details: payoutDetails }).then(res => res.data),

  // The marketing site's free generators (no sign-up): tool is ideas | story | titles
  generateWithTool: (tool, topic, language) =>
    axiosInstance.post('/public/tools/generate', { tool, topic, language }).then(res => res.data),

  // The free YouTube monetization checker (no sign-up): start one on a channel or video link,
  // then read it back, which is also what a shared report's link loads
  startCheck: (url, locale) => axiosInstance.post('/public/checker/checks', { url, locale }).then(res => res.data),
  getCheck: (id) => axiosInstance.get(`/public/checker/checks/${encodeURIComponent(id)}`).then(res => res.data),

  // What a bundle costs where this visitor is (the country comes from Cloudflare)
  getLocalPricing: () => axiosInstance.get('/subscription/pricing').then(res => res.data),

  getAIAgentProject: (projectId) => axiosInstance.get(`/ai-agent/projects/${projectId}`).then(res => res.data),
  // One designed scene designed again with the owner's words (the assistant's redesign_scene); the project GET folds it in
  redesignAIAgentScene: (projectId, sceneIndex, request) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/scenes/${sceneIndex}/design`, { request }).then(res => res.data),

  // Scene clips: one short animated scene with its own sound (routes/scene.py).
  // Reading an idea is asked at Generate, so it never waits long: past 4 s the page
  // makes the narrated video it always made
  readAIAgentIdea: (idea) => axiosInstance.post('/ai-agent/scenes/read', { idea }, { timeout: 4000 }).then(res => res.data),
  // { idea, language, aspect_ratio, source, shape }
  createScene: (data) => axiosInstance.post('/ai-agent/scenes', data).then(res => res.data),
  // The scene page's poll: it also moves the scene along while it is being made
  getScene: (projectId) => axiosInstance.get(`/ai-agent/scenes/${projectId}`).then(res => res.data),
  // The caller's scenes, newest first: { scenes: [{ id, title, idea, poster_url, video_url, seconds, aspect_ratio, created_at, status? }] }
  listScenes: () => axiosInstance.get('/ai-agent/scenes').then(res => res.data),
  markSceneSaved: (projectId, via) => axiosInstance.post(`/ai-agent/scenes/${projectId}/saved`, { via }).then(res => res.data),

  // Remix an AI-clipping clip into an AI Agent video: { project_id, short_id, preset, instructions, language, voice_id }
  createRemix: (data) => axiosInstance.post('/ai-agent/remix', data).then(res => res.data),
  // Remix v2: three angles written for a clip (routes/remix.py remix_angles)
  remixAngles: (data) => axiosInstance.post('/ai-agent/remix/angles', data).then(res => res.data),
  // Remix v2: the owner's edits (routes/remix.py update_remix_v2)
  updateRemixV2: (projectId, edits) => axiosInstance.put(`/ai-agent/remix/${projectId}/v2`, { edits }).then(res => res.data),
  // Remix v2: the source's shots near the clip, and one put on a narrated beat ({ scene, slide, shot }; answers { slide })
  remixV2Shots: (projectId) => axiosInstance.get(`/ai-agent/remix/${projectId}/v2/shots`).then(res => res.data),
  updateRemixV2Shot: (projectId, change) => axiosInstance.put(`/ai-agent/remix/${projectId}/v2/shot`, change).then(res => res.data),
  // A remix's title card: { kicker, headline }; an empty headline removes it
  updateRemixHook: (projectId, hook) => axiosInstance.put(`/ai-agent/remix/${projectId}/hook`, hook).then(res => res.data),
  // Any AI Agent video's title card (a remix's goes through its own route above)
  updateTitleCard: (projectId, hook) => axiosInstance.put(`/ai-agent/projects/${projectId}/hook`, hook).then(res => res.data),
  // A motion scene's graphic (agent/agent/motion_graphics.py): new words and numbers, or { remove: true }; answers { visual }
  updateSceneVisual: (projectId, sceneIndex, body) => axiosInstance.put(`/ai-agent/projects/${projectId}/scenes/${sceneIndex}/visual`, body).then(res => res.data),
  // The remixes made of an AI-clipping project's clips: { remixes: [{ id, short_id, title, status }] }
  listClipRemixes: (projectId) => axiosInstance.get(`/ai-agent/remix/by-project/${projectId}`).then(res => res.data),
  // The notes over one stretch of a remix's clip: [{ text, start, end, sticker }]
  updateRemixCallouts: (projectId, sceneIndex, callouts) =>
    axiosInstance.put(`/ai-agent/remix/${projectId}/scenes/${sceneIndex}/callouts`, { callouts }).then(res => res.data),

  listAIAgentProjects: () => axiosInstance.get('/ai-agent/projects').then(res => res.data),

  // Send any message to the AI agent (feedback, refinement, etc.)
  // The agent handles conversation context automatically via ADK sessions
  sendAIAgentMessage: (projectId, message) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/message`, { message }).then(res => res.data),

  // Trigger media generation from the agent's script
  generateAIAgentVideo: (projectId) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/generate`).then(res => res.data),

  // Posting a finished video (components/publish/PublishDialog): the words for
  // each platform, and posts scheduled for later (emailed as reminders)
  writePostCopy: (projectId, shortId = null) =>
    axiosInstance.post('/publishing/copy', { project_id: projectId, short_id: shortId }).then(res => res.data),
  schedulePost: (post) => axiosInstance.post('/publishing/posts', post).then(res => res.data),
  listScheduledPosts: (projectId) =>
    axiosInstance.get('/publishing/posts', { params: { project_id: projectId } }).then(res => res.data.posts),
  // Where the owner says they posted an AI Agent video; [] takes the mark off
  markPosted: (projectId, platforms) =>
    axiosInstance.put('/publishing/posted', { project_id: projectId, platforms }).then(res => res.data.posted),
  cancelScheduledPost: (postId) => axiosInstance.delete(`/publishing/posts/${postId}`).then(res => res.data),

  // The same AI Agent video in another language: a new project (one AI video)
  translateAIAgentVideo: (projectId, language) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/translate`, { language }).then(res => res.data),
  // Your own voice (components/voices, api/routes/voices.py): made from the user reading
  // a sentence we show them; the recording is sent as the browser made it
  listVoices: () => axiosInstance.get('/voices').then(res => res.data),
  voiceConsent: (language) => axiosInstance.post('/voices/consent', { language }).then(res => res.data),
  createVoice: (recording, name = '') =>
    axiosInstance.post('/voices', recording, {
      params: { name },
      headers: { 'Content-Type': recording.type || 'application/octet-stream' },
      timeout: 120000,
    }).then(res => res.data),
  deleteVoice: (voiceId) => axiosInstance.delete(`/voices/${voiceId}`).then(res => res.data),
  // A series of AI Agent videos drafted on a schedule (pages/SeriesPage, api/routes/series.py):
  // each episode waits for its owner's review, nothing is posted for them
  listSeries: () => axiosInstance.get('/series').then(res => res.data.series),
  createSeries: (series) => axiosInstance.post('/series', series).then(res => res.data),
  updateSeries: (seriesId, changes) => axiosInstance.put(`/series/${seriesId}`, changes).then(res => res.data),
  deleteSeries: (seriesId) => axiosInstance.delete(`/series/${seriesId}`).then(res => res.data),
  // Episodes now, besides the schedule (the schedule stays as it is)
  // A series' first episodes before it exists (POST /series/preview): free; they become its Up next
  previewSeries: (body) => axiosInstance.post('/series/preview', body, { timeout: 90000 }).then(res => res.data),
  // A series proposed from a channel the user likes: its niche, an angle of its own
  seriesInspiration: (link, locale) =>
    axiosInstance.post('/series/inspiration', { link, locale }, { timeout: 90000 }).then(res => res.data),
  makeEpisodesNow: (seriesId, count = 1, itemId = null) =>
    axiosInstance.post(`/series/${seriesId}/episodes`, { count, ...(itemId ? { item_id: itemId } : {}) }, { timeout: 120000 }).then(res => res.data),
  // The next ideas, queued before anything is made; the owner reorders, edits, swaps or adds to them
  // The owner's thumbs on an episode: up, down, or null to take it back
  // An episode watched in the series' review counts as opened (the review brake lets go)
  // The episode made again in its place, same number (the old video and its file go)
  redoEpisode: (projectId, idea = null) =>
    axiosInstance.post(`/series/episodes/${projectId}/redo`, idea || {}).then(res => res.data),
  // The idea a remake would be made from, to read and change first (nothing is made)
  // The idea the episode was made from; fresh: a new one from the writer
  redoEpisodeDraft: (projectId, fresh = false) =>
    axiosInstance.post(`/series/episodes/${projectId}/redo/draft`, fresh ? { fresh: true } : {}, { timeout: 60000 }).then(res => res.data),
  markEpisodeSeen: (projectId) => axiosInstance.post(`/series/episodes/${projectId}/seen`).then(res => res.data),
  setEpisodeFeedback: (projectId, value) => axiosInstance.put(`/series/episodes/${projectId}/feedback`, { value }).then(res => res.data),
  getUpNext: (seriesId) => axiosInstance.get(`/series/${seriesId}/upnext`, { timeout: 60000 }).then(res => res.data.planned),
  editUpNext: (seriesId, items) => axiosInstance.put(`/series/${seriesId}/upnext`, { items }).then(res => res.data.planned),
  swapUpNext: (seriesId, itemId) =>
    axiosInstance.post(`/series/${seriesId}/upnext/${itemId}/swap`, {}, { timeout: 60000 }).then(res => res.data.planned),
  // A recurring cast (components/characters, api/routes/characters.py): drawn once,
  // the same face in every picture of every video that picks them
  listCharacters: () => axiosInstance.get('/characters').then(res => res.data),
  // An uploaded picture looked at before a character is drawn from it: {kind, description, photographic_styles}
  lookAtCharacterPicture: (picture, locale) =>
    axiosInstance.post('/characters/look', { picture, locale }, { timeout: 60000 }).then(res => res.data),
  createCharacter: (character) => axiosInstance.post('/characters', character, { timeout: 120000 }).then(res => res.data),
  redrawCharacter: (characterId, changes = {}) =>
    axiosInstance.post(`/characters/${characterId}/portrait`, changes, { timeout: 120000 }).then(res => res.data),
  // One of its earlier portraits, by number, made the character's again (no drawing)
  chooseCharacterPortrait: (characterId, rev) =>
    axiosInstance.put(`/characters/${characterId}/portrait`, { rev }).then(res => res.data),
  renameCharacter: (characterId, name) => axiosInstance.put(`/characters/${characterId}`, { name }).then(res => res.data),
  deleteCharacter: (characterId) => axiosInstance.delete(`/characters/${characterId}`).then(res => res.data),

  // The video's background music (utils/music.js): a library mood, or null for
  // none, and its level
  // A track of the library (hooks/useMusicCatalog.js), or a mood alone (its own track)
  updateAIAgentMusic: (projectId, mood, volume, track) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/music`, { mood, volume, ...(track ? { track } : {}) }).then(res => res.data.music),

  // The video's sound effects (utils/sfx.js cue lists): {enabled, volume, scenes: [[cue]]}
  updateAIAgentSound: (projectId, body) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/sound`, body).then(res => res.data),
  // The text on each scene: {scenes: [[{text, at, duration, y, style, size}]]}, one list per scene
  updateAIAgentTexts: (projectId, body) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/texts`, body).then(res => res.data),
  // The generated layers as the timeline edits them: {scenes: [{beats, leak, visual, hook?}]}, one per scene
  updateAIAgentLayers: (projectId, body) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/layers`, body).then(res => res.data),
  // Move or delete whole scenes: {keep: [old scene index, in the new order]} -> {scenes}
  arrangeAIAgentScenes: (projectId, keep) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/scenes`, { keep }).then(res => res.data),

  // The video's caption style, kept so it opens with it again (and, on a series
  // episode, as the series' captions)
  // What the owner thinks of the video (a thumb and a few words, api/services/video_feedback.py)
  getAIAgentFeedback: (projectId) =>
    axiosInstance.get(`/ai-agent/projects/${projectId}/feedback`).then(res => res.data.feedback),
  putAIAgentFeedback: (projectId, { rating, text, locale }) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/feedback`, { rating, text, locale }).then(res => res.data.feedback),
  // `shown`: the style the page showed when the video had none saved (its default), so History says what changed from it
  updateAIAgentCaptionStyle: (projectId, captionStyle, shown = null) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/caption-style`, { caption_style: captionStyle, ...(shown ? { shown } : {}) }).then(res => res.data.caption_style),

  // Render AI Agent video with captions and optional custom scenes data
  renderAIAgentVideo: (projectId, captionStyle, scenes = null) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/render`, {
      caption_style: captionStyle,
      scenes: scenes  // Optional: if provided, use these scenes instead of fetching from DB
    }).then(res => res.data),

  // A new scene from the agent (POST …/scenes/insert, services/scene_insert.py): voiced, drawn and put in its place
  insertAIAgentScene: (projectId, body) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/scenes/insert`, body, { timeout: 180000 }).then(res => res.data),

  // Get AI Agent render job status
  getAIAgentRenderStatus: (projectId, jobId) =>
    axiosInstance.get(`/ai-agent/projects/${projectId}/render/${jobId}`).then(res => res.data),

  // Replace AI Agent scene asset (from Pexels or uploaded file)
  // The page's assistant: a request about the video becomes a plan of edits (it changes nothing)
  planAIAgentEdit: (projectId, body) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/assistant`, body).then(res => res.data),
  // One call of the tool-loop agent (services/video_agent.py): the conversation and what the page knows
  // At most AGENT_TURN_WAIT: the API ends a call inside 70 s (services/video_agent.py TURN_SECONDS) and Cloudflare cuts
  // one at 100 s. With no limit, Rawan's first request (2026-10-28) waited until the connection broke at about 2 minutes
  agentTurn: (projectId, body) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/agent`, body, { timeout: AGENT_TURN_WAIT }).then(res => res.data),
  replaceAIAgentAsset: (projectId, data) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/assets`, data).then(res => res.data),

  // Add new slide to a scene (from Pexels or uploaded file)
  addAIAgentSlide: (projectId, data) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/assets/add-slide`, data).then(res => res.data),

  // Delete a slide from a scene
  deleteAIAgentSlide: (projectId, data) =>
    axiosInstance.delete(`/ai-agent/projects/${projectId}/assets/delete-slide`, { data }).then(res => res.data),

  // Reorder slides within a scene
  reorderAIAgentSlides: (projectId, data) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/assets/reorder-slides`, data).then(res => res.data),

  // Get presigned URL for uploading asset
  getAIAgentAssetUploadUrl: (projectId, sceneIndex, slideIndex, filename, contentType) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/assets/upload-url`, {
      scene_index: sceneIndex,
      slide_index: slideIndex,
      filename: filename,
      content_type: contentType,
    }).then(res => res.data),

  // Regenerate a slide with AI using a new prompt (can take 5-10 seconds)
  regenerateAIAgentSlide: (projectId, data) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/assets/regenerate-slide`, data, { timeout: 60000 }).then(res => res.data),

  // Edit an existing slide with AI using edit instructions (Gemini)
  editAIAgentSlide: (projectId, data) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/assets/edit-slide`, data, { timeout: 60000 }).then(res => res.data),

  // Animate an existing slide to video (P-Video, else Wan; 15-40 s, the server gives up past ~100 s)
  animateAIAgentSlide: (projectId, data) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/assets/animate-slide`, data, { timeout: 120000 }).then(res => res.data),

  // The slide's picture speaks the narration heard over it (P-Video with that audio; admins only for now)
  lipSyncAIAgentSlide: (projectId, data) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/assets/lipsync-slide`, data, { timeout: 180000 }).then(res => res.data),

  // Where a picture is kept in view when the video's shape crops it ({x, y} 0-1, or none for the centre)
  setAIAgentSlideFocus: (projectId, data) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/assets/slide-focus`, data).then(res => res.data),

  // An animated slide goes back to the picture it was made from
  restoreAIAgentSlideStill: (projectId, data) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/assets/restore-still`, data).then(res => res.data),

  // Get scene transcription data
  getAIAgentTranscription: (projectId, sceneIndex) =>
    axiosInstance.get(`/ai-agent/projects/${projectId}/transcription/${sceneIndex}`).then(res => res.data),

  // Update scene transcription text
  updateAIAgentTranscription: (projectId, data) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/transcription`, data).then(res => res.data),

  // Update scene narration and regenerate audio with new/existing voice (can take 5-10 seconds)
  // The video's versions (services/versions.py): {current, versions: [{number, at, by, changes}]}, newest first
  listAIAgentVersions: (projectId) => axiosInstance.get(`/ai-agent/projects/${projectId}/versions`).then(res => res.data),
  restoreAIAgentVersion: (projectId, number) => axiosInstance.post(`/ai-agent/projects/${projectId}/versions/${number}/restore`).then(res => res.data),
  // One version's changes taken back, everything since kept (services/versions.py revert)
  revertAIAgentVersion: (projectId, number) => axiosInstance.post(`/ai-agent/projects/${projectId}/versions/${number}/revert`).then(res => res.data),
  // Stock video from Pexels through the API (routes/pexels.py): {videos: [{id, image, duration, video_files}]}
  searchPexelsVideos: (params) => axiosInstance.get('/pexels/videos/search', { params }).then(res => res.data),
  updateAIAgentNarration: (projectId, data) =>
    axiosInstance.put(`/ai-agent/projects/${projectId}/narration`, data, { timeout: 60000 }).then(res => res.data),
  // Every scene in another voice, on the server: returns {voice_change}, and the project
  // carries its progress (ai_agent_json.voice_change) until it is done
  // With `character`, only that character's voice changes (and only the scenes they speak in)
  changeAIAgentVoice: (projectId, voiceId, character) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/voice`, character ? { voice_id: voiceId, character } : { voice_id: voiceId }).then(res => res.data),
  // Back to the voice before the last change (project.voice_undo): {voice_id}, and for a
  // character's change also {character, character_voice}
  undoAIAgentVoiceChange: (projectId) =>
    axiosInstance.post(`/ai-agent/projects/${projectId}/voice/undo`).then(res => res.data),
}; 

// Export axios instance for testing and advanced usage
export { axiosInstance };
